/**
 * Enhanced Validation Rules for WCAG 2.0 Level AA (3.3.3 Error Suggestion)
 * 
 * Provides specific, actionable error messages with examples and suggestions
 * to help users understand and correct validation errors.
 */

/**
 * Email validation with specific format suggestions
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const emailRules = (t) => [
    v => !!v || t('validation.emailRequired'),
    v => {
         if (!v) return true;
         const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
         return emailRegex.test(String(v).toLowerCase()) || t('validation.emailInvalid');
     },
    v => (v && v.length >= 6 && v.length <= 256) || t('validation.emailLength'),
    v => {
        if (!v || !v.includes('@')) return true;
        const localPart = v.substring(0, v.indexOf("@"));
        return localPart.length <= 64 || t('validation.emailLocalPartTooLong');
    }
];

/**
 * Password validation with specific requirement details
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const passwordRules = (t) => [
    v => !!v || t('validation.passwordRequired'),
    v => {
        if (!v) return true;
        if (v.length < 8 || v.length > 30) {
            return t('validation.passwordLength');
        }
        return true;
    },
    v => {
        if (!v) return true;
        const hasUpperCase = /[A-Z]/.test(v);
        const hasLowerCase = /[a-z]/.test(v);
        const hasNumber = /\d/.test(v);
        const hasSpecialChar = /[!@#$%^&*()_+}{:;'?/.,<>]/.test(v);

        if (!hasUpperCase) return t('validation.passwordNeedsUppercase');
        if (!hasLowerCase) return t('validation.passwordNeedsLowercase');
        if (!hasNumber) return t('validation.passwordNeedsNumber');
        if (!hasSpecialChar) return t('validation.passwordNeedsSpecialChar');

        return true;
    }
];

/**
 * Password confirmation validation
 * @param {Function} t - i18n translation function
 * @param {String} passwordValue - The original password to compare against
 * @returns {Array} Validation rules
 */
export const passwordConfirmationRules = (t, passwordValue) => [
    v => !!v || t('validation.passwordConfirmRequired'),
    v => (v && v === passwordValue) || t('validation.passwordMismatch')
];

/**
 * Username validation with character restrictions
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const usernameRules = (t) => [
    v => !!v || t('validation.usernameRequired'),
    v => {
        if (!v) return true;
        const invalidChars = String(v).match(/[^\w@.À-ú-]/gs);
        return !invalidChars || t('validation.usernameInvalidChars');
    },
    v => (v && v.length >= 3) || t('validation.usernameMinLength'),
    v => (v && v.length <= 50) || t('validation.usernameMaxLength')
];

/**
 * First name validation
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const firstNameRules = (t) => [
    v => !!v || t('validation.firstNameRequired'),
    v => {
         if (!v) return true;
         const invalidChars = String(v).match(/[^a-zA-Z\d 'À-ú-]/gs);
         return !invalidChars || t('validation.nameInvalidChars');
     },
     v => (v && v.length <= 100) || t('validation.firstNameMaxLength')
];

/**
 * Last name validation
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const lastNameRules = (t) => [
    v => !!v || t('validation.lastNameRequired'),
    v => {
         if (!v) return true;
         const invalidChars = String(v).match(/[^a-zA-Z\d 'À-ú-]/gs);
         return !invalidChars || t('validation.nameInvalidChars');
     },
     v => (v && v.length <= 100) || t('validation.lastNameMaxLength')
];

/**
 * Phone number validation (North American format)
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const phoneNumberRules = (t) => [
    v => {
        if (!v) return true; // Optional field
        const phoneRegex = /^[\d\s()+-]+$/;
        return phoneRegex.test(v) || t('validation.phoneInvalid');
    },
    v => {
        if (!v) return true;
        const digitsOnly = v.replace(/\D/g, '');
        return digitsOnly.length === 10 || digitsOnly.length === 11 || t('validation.phoneLength');
    }
];

/**
 * Required field validation (generic)
 * @param {Function} t - i18n translation function
 * @param {String} fieldName - Optional field name for personalized message
 * @returns {Array} Validation rules
 */
export const requiredFieldRules = (t, fieldName = null) => [
    v => {
        // Handle different types of empty values
        if (v === null || v === undefined || v === '') return fieldName 
            ? t('validation.fieldRequiredNamed', { field: fieldName })
            : t('validation.fieldRequired');
        if (Array.isArray(v) && v.length === 0) return fieldName
            ? t('validation.fieldRequiredNamed', { field: fieldName })
            : t('validation.fieldRequired');
        return true;
    }
];

/**
 * Min/Max length validation
 * @param {Function} t - i18n translation function
 * @param {Number} min - Minimum length
 * @param {Number} max - Maximum length
 * @returns {Array} Validation rules
 */
export const lengthRules = (t, min = null, max = null) => {
    const rules = [];

    if (min !== null) {
        rules.push(v => {
            if (!v) return true;
            return v.length >= min || t('validation.minLength', { min });
        });
    }

    if (max !== null) {
        rules.push(v => {
            if (!v) return true;
            return v.length <= max || t('validation.maxLength', { max });
        });
    }

    return rules;
};

/**
 * Validation code (MFA/OTP) validation
 * @param {Function} t - i18n translation function
 * @param {Number} length - Expected code length (default 7)
 * @returns {Array} Validation rules
 */
export const validationCodeRules = (t, length = 7) => [
    v => !!v || t('validation.codeRequired'),
    v => {
        if (!v) return true;
        return v.length === length || t('validation.codeLength', { length });
    },
    v => {
        if (!v) return true;
        return /^\d+$/.test(v) || t('validation.codeNumeric');
    }
];

/**
 * URL validation
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const urlRules = (t) => [
    v => {
        if (!v) return true; // Optional
        try {
            new URL(v);
            return true;
        } catch {
            return t('validation.urlInvalid');
        }
    }
];

/**
 * Date validation
 * @param {Function} t - i18n translation function
 * @returns {Array} Validation rules
 */
export const dateRules = (t) => [
    v => {
        if (!v) return true; // Optional
        const date = new Date(v);
        return !Number.isNaN(date.getTime()) || t('validation.dateInvalid');
    }
];

/**
 * Date range validation (from/to)
 * @param {Function} t - i18n translation function
 * @param {String} fromDate - Start date
 * @param {String} toDate - End date
 * @returns {Object} Validation rules for both dates
 */
export const dateRangeRules = (t, fromDate, toDate) => ({
    from: [
        v => {
            if (!v || !toDate) return true;
            return new Date(v) <= new Date(toDate) || t('validation.dateFromAfterTo');
        }
    ],
    to: [
        v => {
            if (!v || !fromDate) return true;
            return new Date(v) >= new Date(fromDate) || t('validation.dateToBeforeFrom');
        }
    ]
});

/**
 * Numeric validation
 * @param {Function} t - i18n translation function
 * @param {Number} min - Minimum value
 * @param {Number} max - Maximum value
 * @returns {Array} Validation rules
 */
export const numericRules = (t, min = null, max = null) => {
    const rules = [
        v => {
            if (v === null || v === undefined || v === '') return true;
            return !Number.isNaN(Number(v)) || t('validation.numericInvalid');
        }
    ];

    if (min !== null) {
        rules.push(v => {
            if (v === null || v === undefined || v === '') return true;
            return Number(v) >= min || t('validation.numericMin', { min });
        });
    }

    if (max !== null) {
        rules.push(v => {
            if (v === null || v === undefined || v === '') return true;
            return Number(v) <= max || t('validation.numericMax', { max });
        });
    }

    return rules;
};

/**
 * File upload validation
 * @param {Function} t - i18n translation function
 * @param {Array} allowedExtensions - Allowed file extensions (e.g., ['.pdf', '.doc'])
 * @param {Number} maxSizeMB - Maximum file size in MB
 * @returns {Array} Validation rules
 */
export const fileRules = (t, allowedExtensions = [], maxSizeMB = null) => {
    const rules = [];

    if (allowedExtensions.length > 0) {
        rules.push(v => {
            if (!v) return true;
            const fileName = v.name || v;
            const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
            return allowedExtensions.includes(extension) || 
                t('validation.fileTypeInvalid', { types: allowedExtensions.join(', ') });
        });
    }

    if (maxSizeMB !== null) {
        rules.push(v => {
            if (!v || !v.size) return true;
            const sizeMB = v.size / (1024 * 1024);
            return sizeMB <= maxSizeMB || t('validation.fileSizeInvalid', { max: maxSizeMB });
        });
    }

    return rules;
};

/**
 * Helper: Combine multiple validation rule sets
 * @param  {...Array} ruleSets - Multiple rule arrays to combine
 * @returns {Array} Combined validation rules
 */
export const combineRules = (...ruleSets) => {
    return ruleSets.flat();
};

export default {
    emailRules,
    passwordRules,
    passwordConfirmationRules,
    usernameRules,
    firstNameRules,
    lastNameRules,
    phoneNumberRules,
    requiredFieldRules,
    lengthRules,
    validationCodeRules,
    urlRules,
    dateRules,
    dateRangeRules,
    numericRules,
    fileRules,
    combineRules
};
